/**
 * wa.me link building for the storefront's two customer-initiated WhatsApp
 * entry points: "ask about this product" on the PDP and the floating help
 * bubble. Unrelated to `@/lib/whatsapp`, which is the outbound Cloud API
 * notification service — this never sends anything, it only builds a URL the
 * customer clicks.
 */

import type { StoreSettings } from '@/lib/brand/types';

/**
 * Digits wa.me will accept, or null when the stored value can't be used.
 *
 * A leading `00` is the international dialling prefix and is dropped. A single
 * leading `0` is deliberately *kept*: it usually means the merchant saved a
 * local number, and guessing a country code for them would silently route
 * customers to a stranger. wa.me rejects it, which is the honest outcome.
 */
export function normalizeWhatsAppPhone(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null;
  let digits = raw.replace(/\D/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);
  // Shortest national numbers run ~8 digits; E.164 caps at 15.
  if (digits.length < 8 || digits.length > 15) return null;
  return digits;
}

/** The store's public WhatsApp number, falling back to its phone number. */
export function resolveStoreWhatsAppPhone(
  settings: StoreSettings | null | undefined,
): string | null {
  return (
    normalizeWhatsAppPhone(settings?.whatsapp) ??
    normalizeWhatsAppPhone(settings?.phone)
  );
}

/**
 * The first number from `candidates` that wa.me can actually dial.
 *
 * `normalizeWhatsAppPhone` deliberately returns a local number with its leading
 * zero intact rather than guessing a country code — see its note. That is the
 * right call, but it means a non-null result is not necessarily a *usable* one,
 * and a link built from one opens WhatsApp on a number it cannot resolve.
 *
 * So when several sources are available (the store's public number, then the
 * WhatsApp integration's), prefer one in international form. A store whose only
 * number is local gets null, and the caller hides its button — no button beats a
 * button that fails.
 */
export function pickDialableWhatsAppPhone(
  candidates: ReadonlyArray<string | null | undefined>,
): string | null {
  for (const candidate of candidates) {
    const digits = normalizeWhatsAppPhone(candidate);
    // A surviving leading zero means a national number with no country code.
    if (digits && !digits.startsWith('0')) return digits;
  }
  return null;
}

export function buildWhatsAppUrl(phone: string, message: string): string {
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
}

/** "I have a question about this product" + the product and its URL. */
export function productEnquiryMessage({
  productName,
  productUrl,
  isRTL,
}: {
  productName: string;
  productUrl: string;
  isRTL: boolean;
}): string {
  return isRTL
    ? `مرحباً، لدي سؤال عن هذا المنتج:\n${productName}\n${productUrl}`
    : `Hello, I have a question about this product:\n${productName}\n${productUrl}`;
}

/** Opening line for the floating help bubble. */
export function supportMessage({
  storeName,
  isRTL,
}: {
  storeName?: string | null;
  isRTL: boolean;
}): string {
  if (isRTL) {
    return storeName
      ? `مرحباً ${storeName}، أحتاج إلى مساعدة.`
      : 'مرحباً، أحتاج إلى مساعدة.';
  }
  return storeName
    ? `Hello ${storeName}, I need some help.`
    : 'Hello, I need some help.';
}
